/**
 * Utility functions for date formatting
 */

/**
 * Format a date string to DD/MM/YYYY format
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string in DD/MM/YYYY format, or "-" if invalid
 */
export const formatDate = (dateString) => {
  if (!dateString) return "-";
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return "-";
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    return "-";
  }
};

/**
 * Format a date string to YYYY-MM-DD format (for input fields)
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string in YYYY-MM-DD format
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return "";
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    return "";
  }
};

/**
 * Get current date in YYYY-MM-DD format
 * @returns {string} Current date in YYYY-MM-DD format
 */
export const getCurrentDate = () => {
  return formatDateForInput(new Date());
};

/**
 * Parse DD/MM/YYYY format to Date object
 * @param {string} dateString - Date string in DD/MM/YYYY format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseDateString = (dateString) => {
  if (!dateString) return null;
  
  try {
    const parts = dateString.split("/");
    if (parts.length !== 3) return null;
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    
    // Validate the date
    if (
      date.getDate() === day &&
      date.getMonth() === month &&
      date.getFullYear() === year
    ) {
      return date;
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Get a Date from a report object (handles date string DD/MM/YYYY, ISO string, or createdAt)
 * @param {object} report - Report object with date and/or createdAt
 * @returns {Date|null} Date or null if invalid
 */
export const getReportDate = (report) => {
  if (!report) return null;
  if (report.date) {
    if (typeof report.date === "string" && report.date.includes("/")) {
      const parsed = parseDateString(report.date);
      return parsed || null;
    }
    const d = new Date(report.date);
    return isNaN(d.getTime()) ? null : d;
  }
  if (report.createdAt?.toDate) return report.createdAt.toDate();
  if (report.createdAt) {
    const d = new Date(report.createdAt);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/**
 * Get start and end Date (00:00:00) for an ISO week number in a given year.
 * ISO week 1 is the week containing January 4.
 * @param {number} year - Full year
 * @param {number} weekNum - Week number 1-53
 * @returns {{ start: Date, end: Date }} Start (Monday) and end (Sunday) of the week
 */
export const getISOWeekStartEnd = (year, weekNum) => {
  const jan4 = new Date(year, 0, 4);
  const dayOfJan4 = jan4.getDay(); // 0=Sun, 1=Mon, ...
  const mondayOffset = (dayOfJan4 + 6) % 7; // Mon=0, Sun=6
  const week1Monday = new Date(year, 0, 4 - mondayOffset);
  const start = new Date(week1Monday);
  start.setDate(week1Monday.getDate() + (weekNum - 1) * 7);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

/**
 * Get number of ISO weeks in a year (52 or 53)
 * @param {number} year - Full year
 * @returns {number} 52 or 53
 */
export const getWeeksInYear = (year) => {
  const dec28 = new Date(year, 11, 28);
  const jan4 = new Date(year + 1, 0, 4);
  const dayOfDec28 = dec28.getDay();
  const dayOfJan4 = jan4.getDay();
  const monDec28 = (dayOfDec28 + 6) % 7;
  const monJan4 = (dayOfJan4 + 6) % 7;
  if (monDec28 === 4 || monJan4 === 3) return 53;
  return 52;
};

/**
 * Get ISO week number (1-53) for a given date
 * @param {Date} date - Date
 * @returns {number} ISO week number
 */
export const getISOWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const dayOfJan4 = jan4.getDay();
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - ((dayOfJan4 + 6) % 7));
  const dayOfYear = Math.floor((d - week1Monday) / 86400000);
  return Math.ceil((dayOfYear + 1) / 7);
};

